import type { LucideIcon } from 'lucide-react'
import {
  Compass,
  Crosshair,
  Eye,
  History,
  ScanLine,
  Shuffle,
  Smartphone,
  Users,
  Video,
  Wifi,
} from 'lucide-react'

/**
 * Landing page copy and data.
 *
 * This is the file to edit when a feature ships: add an entry to `FEATURES`,
 * or move one out of `COMING_SOON`. No JSX lives here on purpose — the
 * rendering is in `LandingSections.tsx` and never needs to change.
 */

/** Mirrors SpotlightCard's `spotlightColor` prop so a bad literal fails here. */
type SpotlightColor = `rgba(${number}, ${number}, ${number}, ${number})`

/** The accent families defined in packages/tailwind/base.css. */
export type LandingAccent = 'brand' | 'info' | 'success' | 'warning'

interface AccentStyles {
  /** Icon chip background. */
  chip: string
  /** Icon foreground. */
  icon: string
  /** Hover glow, hue-matched to the chip. */
  spotlight: SpotlightColor
}

/**
 * Chip, icon and glow have to stay the same hue, so they travel together
 * behind one key. Every class is a complete literal — Tailwind scans source
 * text, so an interpolated `bg-${accent}/20` would compile to nothing.
 *
 * The explicit Record annotation is load-bearing: without it `spotlight`
 * widens to `string` and stops being assignable to SpotlightCard's prop.
 */
export const LANDING_ACCENTS: Record<LandingAccent, AccentStyles> = {
  brand: {
    chip: 'bg-brand/20',
    icon: 'text-brand-muted-foreground',
    spotlight: 'rgba(168, 85, 247, 0.15)',
  },
  info: {
    chip: 'bg-info/20',
    icon: 'text-info',
    spotlight: 'rgba(59, 130, 246, 0.15)',
  },
  success: {
    chip: 'bg-success/20',
    icon: 'text-success',
    spotlight: 'rgba(34, 197, 94, 0.15)',
  },
  warning: {
    chip: 'bg-warning/20',
    icon: 'text-warning',
    spotlight: 'rgba(245, 158, 11, 0.15)',
  },
}

export interface LandingFeature {
  /** Stable React key. */
  id: string
  title: string
  description: string
  icon: LucideIcon
  accent: LandingAccent
  /** Renders a badge next to the icon. Omit for a finished feature. */
  badge?: 'beta'
}

/**
 * Features that are live today.
 *
 * Keep the length a multiple of 3 — the grid is `md:grid-cols-3` and a
 * partial last row reads as a mistake.
 */
export const FEATURES: readonly LandingFeature[] = [
  {
    id: 'battlefield-video',
    icon: Video,
    accent: 'brand',
    title: 'Battlefield Video',
    description:
      "Crystal-clear feeds of every player's battlefield. Read cards, follow the board, and never miss a play — all in real time.",
  },
  {
    id: 'phone-camera',
    icon: Smartphone,
    accent: 'info',
    title: 'Phone as Camera',
    description:
      'Prop up your phone for a top-down board view, then pair it to your game room with a quick link. No extra webcam required.',
  },
  {
    id: 'game-tools',
    icon: Users,
    accent: 'success',
    title: 'Built-in Game Tools',
    description:
      'Track life totals, commander damage, and turn order right beside the action — no extra apps or scratch paper.',
  },
  {
    id: 'card-recognition',
    icon: ScanLine,
    accent: 'warning',
    badge: 'beta',
    title: 'Card Recognition',
    description:
      "Click any card on any feed and we'll look it up — full image, oracle text, straight into your recent cards. Still learning: one tap tells us when it guessed wrong.",
  },
  {
    id: 'seat-order',
    icon: Shuffle,
    accent: 'brand',
    title: 'Seat & Turn Order',
    description:
      'Shuffle the seating with one click, or drag players into the order you want. Turn order settled before the first land drop.',
  },
  {
    id: 'drop-in-play',
    icon: Wifi,
    accent: 'info',
    title: 'Drop-in, Drop-out Play',
    description:
      'Tabs crash, laptops sleep, Wi-Fi drops. Rejoin your room from the front page and pick the game up exactly where it stood.',
  },
]

export type ComingSoonStatus = 'in-progress' | 'planned'

export const COMING_SOON_STATUS_LABELS: Record<ComingSoonStatus, string> = {
  'in-progress': 'In Progress',
  planned: 'Planned',
}

export interface ComingSoonItem {
  id: string
  title: string
  description: string
  icon: LucideIcon
  status: ComingSoonStatus
}

/**
 * What's next. Keep the length even — the grid is
 * `sm:grid-cols-2 lg:grid-cols-4`.
 */
export const COMING_SOON: readonly ComingSoonItem[] = [
  {
    id: 'sharper-recognition',
    icon: Crosshair,
    status: 'in-progress',
    title: 'Sharper Card Recognition',
    description:
      "Trained models replacing today's fallbacks, so a click lands on the right card far more often.",
  },
  {
    id: 'find-a-table',
    icon: Compass,
    status: 'planned',
    title: 'Find a Table',
    description:
      'Public rooms with format and power level, so you can find a pod instead of a group chat.',
  },
  {
    id: 'game-history',
    icon: History,
    status: 'planned',
    title: 'Game History',
    description:
      'Your past games, saved to your account — who played, who won, how long it ran.',
  },
  {
    id: 'spectate-replay',
    icon: Eye,
    status: 'planned',
    title: 'Spectate & Replay',
    description:
      'Watch a game live from the rail, or scroll back through one after it ends.',
  },
]

export interface LandingNavItem {
  label: string
  targetId: string
}

/**
 * One source for the header nav, the mobile sheet and the footer. Order
 * matches the DOM, which is what a smooth-scroll nav needs.
 */
export const LANDING_NAV_ITEMS: LandingNavItem[] = [
  { label: 'Features', targetId: 'features' },
  { label: 'Coming Soon', targetId: 'coming-soon' },
  { label: 'How It Works', targetId: 'how-it-works' },
]
